/**
 * Persists map layer and overlay toggle state in browser cookie `midpointLayerState`.
 * Weather perspective selections (toggles, opacity, nav tab, replay mode, etc.) live here too.
 */

import { DEFAULT_RADAR_MODE, RADAR_MODES } from "../constants/radarMode.js";
import { DEFAULT_ACCUMULATION_WINDOW } from "../constants/rainfallLayers.js";
import { COLOMBIA_SATELLITE_DEFAULT_OPACITY } from "../constants/colombiaSatellitePrecipitation.js";
import { DEFAULT_RADAR_PLAYBACK_RANGE_PRESET } from "./radarPlaybackRange.js";

export const LAYER_STATE_COOKIE = "midpointLayerState";

const COLOMBIA_RADAR_SOURCE_MODES = new Set([
  "preferred",
  "ideam",
  "ideam_bart",
  "rainviewer",
  "legacy_ideam_ospa",
  "valley_preferred",
  "siata_local_only",
]);

const COLOMBIA_RADAR_SOURCE_MODE_ORIGINS = new Set([
  "user_selected",
  "system_default",
  "migration_default",
]);

export const DEFAULT_LAYER_STATE = {
  weather: false,
  infrastructure: false,
  realEstate: false,
  safety: false,
  reports: false,
  weatherRadar: false,
  radarIntensityFilterCode: "ALL",
  radarSourceCode: "RAINVIEWER_RADAR",
  radarProductCode: "COMPOSITE_REFLECTIVITY",
  weatherRadarLegendCollapsed: false,
  weatherRadarContoursEnabled: false,
  weatherRadarCellsEnabled: false,
  weatherRadarCellTracksEnabled: false,
  weatherRadarNowcastEnabled: false,
  colombiaRadarNowcastEnabled: false,
  weatherRadarSiteRangeRingsEnabled: false,
  weatherShowPlannedRadarSites: false,
  weatherValleyFocusModeEnabled: false,
  weatherFocusAreaCode: "ABURRA_VALLEY",
  weatherFocusBufferKm: 50,
  weatherValleyFocusLimitOverlays: false,
  weatherValleyFocusShowBoundary: false,
  weatherValleyFocusShowBuffer: false,
  weatherValleyFocusDimOutside: false,
  weatherRainGaugesEnabled: false,
  rainfallIntensity: false,
  rainfallAccumulation: false,
  floodZones: false,
  hydro: false,
  stormCells: false,
  stormTracks: false,
  stormProjections: false,
  rainGauges: false,
  weatherStations: false,
  streamSensors: false,
  officialAlerts: false,
  midpointAlerts: false,
  communityReports: false,
  precipitationStations: false,
  riverLevels: false,
  environmentalAlerts: false,
  siataAirQuality: false,
  radarLayer: false,
  ideamRadarLayer: false,
  ideamBartOverlay: false,
  siataSantaElenaOverlay: false,
  aerocivilAviationOverlay: false,
  satellitePrecipLayer: false,
  rainviewerBackupRadar: false,
  lightningStrikes: false,
  forecastCards: false,
  neighborhoodRiskBadges: false,
  floodRiskSurface: false,
  communitySignals: false,
  potentialEvents: false,
  predictiveSignals: false,
  weatherAutoRefresh: false,
  weatherAlertsActiveOnly: true,
  infrastructureTypes: [],
  boundaryBarrios: false,
  boundaryComunas: false,
  boundaryMarketZones: false,
  foodStores: false,
  foodSupermarkets: false,
  foodSupermarketBuffers: false,
  foodDiscountStores: false,
  foodBasicBasketAffordability: false,
  foodAccessGaps: false,
  foodWholesaleMarkets: false,
  foodSupplyPressure: false,
  foodInputPressure: false,
  foodPriceHeatmap: false,
  foodBasicBasketZones: false,
  transportMetroStations: false,
  transportMetroLines: false,
  transportBusStops: false,
  transportBusRoutes: false,
  transportOsmCommunity: false,
  transportDiagnostics: false,
  transportMetroRidershipPressure: false,
  transportDisruptionSignals: false,
  transportDisruptionWeatherAlerts: false,
  transportDisruptionFloodContext: false,
  transportDisruptionRainRisk: false,
  transportCommunityMobilityReports: false,
  transportCommunityVerifiedReports: false,
  transportCommunityUnverifiedReports: false,
  transportRoadCorridors: false,
  transportRoadClosures: false,
  transportRoadConstruction: false,
  transportRoadAccidents: false,
  transportRoadFloodLandslide: false,
  transportManualRoadIncidents: false,
  radarOpacity: 0.65,
  satellitePrecipOpacity: COLOMBIA_SATELLITE_DEFAULT_OPACITY,
  colombiaRadarSourceMode: "preferred",
  colombiaRadarSourceModeOrigin: null,
  weatherActiveNavId: "",
  weatherTimeMode: "live",
  weatherRadarMode: DEFAULT_RADAR_MODE,
  weatherRadarPlaybackRangePreset: DEFAULT_RADAR_PLAYBACK_RANGE_PRESET,
  rainfallAccumulationWindow: DEFAULT_ACCUMULATION_WINDOW,
  rainfallOpacity: 0.5,
  lightningSinceMinutes: 60,
  localSensorsPanelEnabled: false,
  stormReplayModeEnabled: false,
  stormReplaySourceCode: "",
  stormReplayProductCode: "",
  stormReplayTimeBucketMinutes: 10,
  stormReplayIncludeLayers: null,
  forecastChartRangeHours: 24,
  historicalChartRangeDays: 7,
  weatherMapLegendVisibility: null,
};

function normalizeLayerState(parsed = {}) {
  return {
    ...DEFAULT_LAYER_STATE,
    ...parsed,
    infrastructureTypes: Array.isArray(parsed.infrastructureTypes)
      ? parsed.infrastructureTypes
      : [],
    radarOpacity:
      parsed.radarOpacity != null && Number.isFinite(Number(parsed.radarOpacity))
        ? Number(parsed.radarOpacity)
        : DEFAULT_LAYER_STATE.radarOpacity,
    satellitePrecipOpacity:
      parsed.satellitePrecipOpacity != null &&
      Number.isFinite(Number(parsed.satellitePrecipOpacity))
        ? Number(parsed.satellitePrecipOpacity)
        : DEFAULT_LAYER_STATE.satellitePrecipOpacity,
    colombiaRadarSourceMode: (() => {
      const mode =
        parsed.colombiaRadarSourceMode === "ideam"
          ? "ideam_bart"
          : parsed.colombiaRadarSourceMode;
      return COLOMBIA_RADAR_SOURCE_MODES.has(mode)
        ? mode
        : DEFAULT_LAYER_STATE.colombiaRadarSourceMode;
    })(),
    colombiaRadarSourceModeOrigin: COLOMBIA_RADAR_SOURCE_MODE_ORIGINS.has(
      parsed.colombiaRadarSourceModeOrigin
    )
      ? parsed.colombiaRadarSourceModeOrigin
      : DEFAULT_LAYER_STATE.colombiaRadarSourceModeOrigin,
    weatherTimeMode: parsed.weatherTimeMode === "replay" ? "replay" : "live",
    weatherRadarMode:
      parsed.weatherRadarMode === RADAR_MODES.STORED_PLAYBACK
        ? RADAR_MODES.STORED_PLAYBACK
        : parsed.weatherRadarMode === RADAR_MODES.STORM_REPLAY
          ? RADAR_MODES.STORM_REPLAY
          : parsed.weatherRadarMode || DEFAULT_LAYER_STATE.weatherRadarMode,
    lightningSinceMinutes:
      parsed.lightningSinceMinutes != null &&
      Number.isFinite(Number(parsed.lightningSinceMinutes))
        ? Number(parsed.lightningSinceMinutes)
        : DEFAULT_LAYER_STATE.lightningSinceMinutes,
    rainfallOpacity:
      parsed.rainfallOpacity != null && Number.isFinite(Number(parsed.rainfallOpacity))
        ? Number(parsed.rainfallOpacity)
        : DEFAULT_LAYER_STATE.rainfallOpacity,
    forecastChartRangeHours: parsed.forecastChartRangeHours === 48 ? 48 : 24,
    historicalChartRangeDays: parsed.historicalChartRangeDays === 30 ? 30 : 7,
    stormReplayIncludeLayers:
      parsed.stormReplayIncludeLayers && typeof parsed.stormReplayIncludeLayers === "object"
        ? parsed.stormReplayIncludeLayers
        : DEFAULT_LAYER_STATE.stormReplayIncludeLayers,
    weatherMapLegendVisibility:
      parsed.weatherMapLegendVisibility && typeof parsed.weatherMapLegendVisibility === "object"
        ? parsed.weatherMapLegendVisibility
        : DEFAULT_LAYER_STATE.weatherMapLegendVisibility,
  };
}

export function setCookie(name, value, days = 30) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

/** Cookie expires at 23:59:59.999 local time so layer choices reset the next calendar day. */
export function getEndOfLocalDayCookieExpiry() {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  return end.toUTCString();
}

export function getCookie(name) {
  const raw = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`))
    ?.split("=")
    .slice(1)
    .join("=");

  return raw ? decodeURIComponent(raw) : null;
}

export function saveLayerState(layerState) {
  const payload = JSON.stringify({
    ...layerState,
    layerStateSavedOn: getLocalDateKey(),
  });
  const expires = getEndOfLocalDayCookieExpiry();
  document.cookie = `${LAYER_STATE_COOKIE}=${encodeURIComponent(payload)}; expires=${expires}; path=/; SameSite=Lax`;
}

function getLocalDateKey(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function stripLayerStateMetadata(parsed = {}) {
  const { layerStateSavedOn: _savedOn, ...layerState } = parsed;
  return layerState;
}

function isLayerStateStale(parsed = {}) {
  return Boolean(parsed.layerStateSavedOn && parsed.layerStateSavedOn !== getLocalDateKey());
}

export function patchLayerState(partial = {}) {
  if (!partial || typeof partial !== "object") {
    return loadLayerState();
  }

  const next = normalizeLayerState({
    ...loadLayerState(),
    ...partial,
  });
  saveLayerState(next);
  return next;
}

export function loadLayerState() {
  try {
    const saved = getCookie(LAYER_STATE_COOKIE);

    if (!saved) {
      return normalizeLayerState();
    }

    const parsed = JSON.parse(saved);
    if (!parsed.layerStateSavedOn || isLayerStateStale(parsed)) {
      return normalizeLayerState();
    }

    return normalizeLayerState(stripLayerStateMetadata(parsed));
  } catch (error) {
    console.warn("Unable to load saved layer state from cookie.", error);
    return normalizeLayerState();
  }
}

/** True when saved cookie state has any essential weather overlay enabled. */
export function hasAnyWeatherOverlayInSavedState(savedState = loadLayerState()) {
  return Boolean(
    savedState.ideamRadarLayer ||
    savedState.ideamBartOverlay ||
      savedState.siataSantaElenaOverlay ||
      savedState.aerocivilAviationOverlay ||
      savedState.rainviewerBackupRadar ||
      savedState.satellitePrecipLayer ||
      savedState.precipitationStations ||
      savedState.riverLevels ||
      savedState.lightningStrikes ||
      savedState.floodRiskSurface ||
      savedState.neighborhoodRiskBadges ||
      savedState.rainfallIntensity ||
      savedState.rainfallAccumulation ||
      savedState.environmentalAlerts ||
      savedState.communitySignals ||
      savedState.potentialEvents ||
      savedState.predictiveSignals
  );
}

/** Apply saved on/off state to API map layer objects. */
export function applySavedLayersToApiLayers(layers, savedState = loadLayerState()) {
  const shouldEnableWeatherMaster =
    Boolean(savedState.weather) || hasAnyWeatherOverlayInSavedState(savedState);

  return layers.map((layer) => ({
    ...layer,
    enabled:
      layer.id === "weather"
        ? shouldEnableWeatherMaster
        : Boolean(savedState[layer.id]),
  }));
}

/** Overlay toggles from cookie (Sidebar / MapView props). */
export function overlayFlagsFromSavedState(savedState = loadLayerState()) {
  return {
    floodZonesEnabled: Boolean(savedState.floodZones),
    hydroEnabled: Boolean(savedState.hydro),
    rainfallIntensityEnabled: Boolean(savedState.rainfallIntensity),
    rainfallAccumulationEnabled: Boolean(savedState.rainfallAccumulation),
    stormCellsEnabled: Boolean(savedState.stormCells),
    stormTracksEnabled: Boolean(savedState.stormTracks),
    stormProjectionsEnabled: Boolean(savedState.stormProjections),
    rainGaugesEnabled: Boolean(savedState.rainGauges),
    weatherStationsEnabled: Boolean(savedState.weatherStations),
    streamSensorsEnabled: Boolean(savedState.streamSensors),
    officialAlertsEnabled: Boolean(savedState.officialAlerts),
    midpointAlertsEnabled: Boolean(savedState.midpointAlerts),
    communityReportsEnabled: Boolean(savedState.communityReports),
    precipitationStationsEnabled: Boolean(savedState.precipitationStations),
    riverLevelsEnabled: Boolean(savedState.riverLevels),
    environmentalAlertsEnabled: Boolean(savedState.environmentalAlerts),
    siataAirQualityEnabled: Boolean(savedState.siataAirQuality),
    radarLayerEnabled: Boolean(savedState.radarLayer),
    ideamRadarLayerEnabled: Boolean(savedState.ideamRadarLayer),
    ideamBartOverlayEnabled: Boolean(savedState.ideamBartOverlay),
    siataSantaElenaOverlayEnabled: Boolean(savedState.siataSantaElenaOverlay),
    aerocivilAviationOverlayEnabled: Boolean(savedState.aerocivilAviationOverlay),
    rainviewerBackupLayerEnabled: Boolean(savedState.rainviewerBackupRadar),
    satellitePrecipLayerEnabled: Boolean(savedState.satellitePrecipLayer),
    lightningStrikesEnabled: Boolean(savedState.lightningStrikes),
    forecastCardsEnabled: Boolean(savedState.forecastCards),
    neighborhoodRiskBadgesEnabled: Boolean(savedState.neighborhoodRiskBadges),
    floodRiskSurfaceEnabled: Boolean(savedState.floodRiskSurface),
    communitySignalsEnabled: Boolean(savedState.communitySignals),
    potentialEventsEnabled: Boolean(savedState.potentialEvents),
    predictiveSignalsEnabled: Boolean(savedState.predictiveSignals),
    weatherAutoRefresh: Boolean(savedState.weatherAutoRefresh),
    weatherAlertsActiveOnly:
      savedState.weatherAlertsActiveOnly !== undefined
        ? Boolean(savedState.weatherAlertsActiveOnly)
        : true,
    infrastructureTypes: savedState.infrastructureTypes || [],
    radarPlaying: Boolean(savedState.weatherRadar),
    radarIntensityFilterCode:
      savedState.radarIntensityFilterCode || DEFAULT_LAYER_STATE.radarIntensityFilterCode,
    radarSourceCode: savedState.radarSourceCode || DEFAULT_LAYER_STATE.radarSourceCode,
    radarProductCode: savedState.radarProductCode || DEFAULT_LAYER_STATE.radarProductCode,
    weatherRadarLegendCollapsed:
      typeof savedState.weatherRadarLegendCollapsed === "boolean"
        ? savedState.weatherRadarLegendCollapsed
        : DEFAULT_LAYER_STATE.weatherRadarLegendCollapsed,
    weatherRadarContoursEnabled: Boolean(savedState.weatherRadarContoursEnabled),
    weatherRadarCellsEnabled: Boolean(savedState.weatherRadarCellsEnabled),
    weatherRadarCellTracksEnabled: Boolean(savedState.weatherRadarCellTracksEnabled),
    weatherRadarNowcastEnabled: Boolean(savedState.weatherRadarNowcastEnabled),
    colombiaRadarNowcastEnabled: Boolean(savedState.colombiaRadarNowcastEnabled),
    weatherRadarSiteRangeRingsEnabled:
      typeof savedState.weatherRadarSiteRangeRingsEnabled === "boolean"
        ? savedState.weatherRadarSiteRangeRingsEnabled
        : undefined,
    weatherShowPlannedRadarSites:
      typeof savedState.weatherShowPlannedRadarSites === "boolean"
        ? savedState.weatherShowPlannedRadarSites
        : DEFAULT_LAYER_STATE.weatherShowPlannedRadarSites,
    weatherValleyFocusModeEnabled: Boolean(savedState.weatherValleyFocusModeEnabled),
    weatherFocusAreaCode: savedState.weatherFocusAreaCode || DEFAULT_LAYER_STATE.weatherFocusAreaCode,
    weatherFocusBufferKm:
      savedState.weatherFocusBufferKm != null
        ? Number(savedState.weatherFocusBufferKm)
        : DEFAULT_LAYER_STATE.weatherFocusBufferKm,
    weatherValleyFocusLimitOverlays: Boolean(savedState.weatherValleyFocusLimitOverlays),
    weatherValleyFocusShowBoundary:
      typeof savedState.weatherValleyFocusShowBoundary === "boolean"
        ? savedState.weatherValleyFocusShowBoundary
        : DEFAULT_LAYER_STATE.weatherValleyFocusShowBoundary,
    weatherValleyFocusShowBuffer:
      typeof savedState.weatherValleyFocusShowBuffer === "boolean"
        ? savedState.weatherValleyFocusShowBuffer
        : DEFAULT_LAYER_STATE.weatherValleyFocusShowBuffer,
    weatherValleyFocusDimOutside:
      typeof savedState.weatherValleyFocusDimOutside === "boolean"
        ? savedState.weatherValleyFocusDimOutside
        : DEFAULT_LAYER_STATE.weatherValleyFocusDimOutside,
    weatherRainGaugesEnabled: Boolean(savedState.weatherRainGaugesEnabled),
    boundaryBarriosEnabled: Boolean(savedState.boundaryBarrios),
    boundaryComunasEnabled: Boolean(savedState.boundaryComunas),
    boundaryMarketZonesEnabled: Boolean(savedState.boundaryMarketZones),
    radarOpacity: savedState.radarOpacity ?? DEFAULT_LAYER_STATE.radarOpacity,
    satellitePrecipOpacity:
      savedState.satellitePrecipOpacity ?? DEFAULT_LAYER_STATE.satellitePrecipOpacity,
    colombiaRadarSourceMode:
      savedState.colombiaRadarSourceMode || DEFAULT_LAYER_STATE.colombiaRadarSourceMode,
    weatherActiveNavId: savedState.weatherActiveNavId || "",
    weatherTimeMode: savedState.weatherTimeMode || DEFAULT_LAYER_STATE.weatherTimeMode,
    weatherRadarMode: savedState.weatherRadarMode || DEFAULT_LAYER_STATE.weatherRadarMode,
    weatherRadarPlaybackRangePreset:
      savedState.weatherRadarPlaybackRangePreset ||
      DEFAULT_LAYER_STATE.weatherRadarPlaybackRangePreset,
    rainfallAccumulationWindow:
      savedState.rainfallAccumulationWindow || DEFAULT_LAYER_STATE.rainfallAccumulationWindow,
    rainfallOpacity: savedState.rainfallOpacity ?? DEFAULT_LAYER_STATE.rainfallOpacity,
    lightningSinceMinutes:
      savedState.lightningSinceMinutes ?? DEFAULT_LAYER_STATE.lightningSinceMinutes,
    localSensorsPanelEnabled: Boolean(savedState.localSensorsPanelEnabled),
    stormReplayModeEnabled: Boolean(savedState.stormReplayModeEnabled),
    stormReplaySourceCode: savedState.stormReplaySourceCode || "",
    stormReplayProductCode: savedState.stormReplayProductCode || "",
    stormReplayTimeBucketMinutes:
      savedState.stormReplayTimeBucketMinutes ?? DEFAULT_LAYER_STATE.stormReplayTimeBucketMinutes,
    stormReplayIncludeLayers: savedState.stormReplayIncludeLayers,
    forecastChartRangeHours:
      savedState.forecastChartRangeHours ?? DEFAULT_LAYER_STATE.forecastChartRangeHours,
    historicalChartRangeDays:
      savedState.historicalChartRangeDays ?? DEFAULT_LAYER_STATE.historicalChartRangeDays,
    weatherMapLegendVisibility: savedState.weatherMapLegendVisibility,
    foodSupermarketsEnabled: Boolean(savedState.foodSupermarkets),
    foodSupermarketBuffersEnabled: Boolean(savedState.foodSupermarketBuffers),
    foodPriceHeatmapEnabled: Boolean(savedState.foodPriceHeatmap),
    foodBasicBasketZonesEnabled: Boolean(savedState.foodBasicBasketZones),
    foodAccessGapsEnabled: Boolean(savedState.foodAccessGaps),
    transportMetroStationsEnabled: Boolean(savedState.transportMetroStations),
    transportMetroLinesEnabled: Boolean(savedState.transportMetroLines),
    transportBusStopsEnabled: Boolean(savedState.transportBusStops),
    transportBusRoutesEnabled: Boolean(savedState.transportBusRoutes),
    transportOsmCommunityEnabled: Boolean(savedState.transportOsmCommunity),
    transportDiagnosticsEnabled: Boolean(savedState.transportDiagnostics),
    transportMetroRidershipPressureEnabled: Boolean(savedState.transportMetroRidershipPressure),
    transportDisruptionSignalsEnabled: Boolean(savedState.transportDisruptionSignals),
    transportDisruptionWeatherAlertsEnabled: Boolean(savedState.transportDisruptionWeatherAlerts),
    transportDisruptionFloodContextEnabled: Boolean(savedState.transportDisruptionFloodContext),
    transportDisruptionRainRiskEnabled: Boolean(savedState.transportDisruptionRainRisk),
    transportCommunityMobilityReportsEnabled: Boolean(
      savedState.transportCommunityMobilityReports
    ),
    transportCommunityVerifiedReportsEnabled: Boolean(savedState.transportCommunityVerifiedReports),
    transportCommunityUnverifiedReportsEnabled: Boolean(
      savedState.transportCommunityUnverifiedReports
    ),
  };
}

/** Build cookie payload from current React state. */
export function buildLayerStateFromApp(appState = {}) {
  const {
    layers = [],
    floodZonesEnabled,
    hydroEnabled,
    rainfallIntensityEnabled,
    rainfallAccumulationEnabled,
    stormCellsEnabled,
    stormTracksEnabled,
    stormProjectionsEnabled,
    rainGaugesEnabled,
    weatherStationsEnabled,
    streamSensorsEnabled,
    officialAlertsEnabled,
    midpointAlertsEnabled,
    communityReportsEnabled,
    precipitationStationsEnabled,
    riverLevelsEnabled,
    environmentalAlertsEnabled,
    siataAirQualityEnabled,
    radarLayerEnabled,
    ideamRadarLayerEnabled,
    ideamBartOverlayEnabled,
    siataSantaElenaOverlayEnabled,
    aerocivilAviationOverlayEnabled,
    rainviewerBackupLayerEnabled,
    satellitePrecipLayerEnabled,
    lightningStrikesEnabled,
    forecastCardsEnabled,
    neighborhoodRiskBadgesEnabled,
    floodRiskSurfaceEnabled,
    communitySignalsEnabled,
    potentialEventsEnabled,
    predictiveSignalsEnabled,
    weatherAutoRefresh,
    weatherAlertsActiveOnly,
    selectedInfrastructureTypes,
    radarPlaying,
    radarIntensityFilterCode,
    radarSourceCode,
    radarProductCode,
    weatherRadarLegendCollapsed,
    weatherRadarContoursEnabled,
    weatherRadarCellsEnabled,
    weatherRadarCellTracksEnabled,
    weatherRadarNowcastEnabled,
    colombiaRadarNowcastEnabled,
    weatherRadarSiteRangeRingsEnabled,
    weatherShowPlannedRadarSites,
    boundaryBarriosEnabled,
    boundaryComunasEnabled,
    boundaryMarketZonesEnabled,
    radarOpacity,
    satellitePrecipOpacity,
    colombiaRadarSourceMode,
    colombiaRadarSourceModeOrigin,
    weatherActiveNavId,
    weatherTimeMode,
    weatherRadarMode,
    weatherRadarPlaybackRangePreset,
    rainfallAccumulationWindow,
    rainfallOpacity,
    lightningSinceMinutes,
    localSensorsPanelEnabled,
    stormReplayModeEnabled,
    stormReplaySourceCode,
    stormReplayProductCode,
    stormReplayTimeBucketMinutes,
    stormReplayIncludeLayers,
    forecastChartRangeHours,
    historicalChartRangeDays,
    weatherMapLegendVisibility,
    foodSupermarketsEnabled,
    foodSupermarketBuffersEnabled,
    foodPriceHeatmapEnabled,
    foodBasicBasketZonesEnabled,
    foodAccessGapsEnabled,
    transportMetroStationsEnabled,
    transportMetroLinesEnabled,
    transportBusStopsEnabled,
    transportBusRoutesEnabled,
    transportOsmCommunityEnabled,
    transportDiagnosticsEnabled,
    transportMetroRidershipPressureEnabled,
    transportDisruptionSignalsEnabled,
    transportDisruptionWeatherAlertsEnabled,
    transportDisruptionFloodContextEnabled,
    transportDisruptionRainRiskEnabled,
    transportCommunityMobilityReportsEnabled,
    transportCommunityVerifiedReportsEnabled,
    transportCommunityUnverifiedReportsEnabled,
  } = appState;

  const layerMap = Object.fromEntries(layers.map((layer) => [layer.id, layer.enabled]));
  const valleyFocusModeEnabled = Boolean(
    appState.weatherValleyFocusModeEnabled ?? DEFAULT_LAYER_STATE.weatherValleyFocusModeEnabled
  );
  const rainGaugesOverlayEnabled = Boolean(
    appState.weatherRainGaugesEnabled ?? DEFAULT_LAYER_STATE.weatherRainGaugesEnabled
  );

  return {
    ...DEFAULT_LAYER_STATE,
    weather: Boolean(layerMap.weather),
    infrastructure: Boolean(layerMap.infrastructure),
    realEstate: Boolean(layerMap.realEstate),
    safety: Boolean(layerMap.safety),
    reports: Boolean(layerMap.reports),
    weatherRadar: Boolean(radarPlaying),
    radarIntensityFilterCode: radarIntensityFilterCode || DEFAULT_LAYER_STATE.radarIntensityFilterCode,
    radarSourceCode: radarSourceCode || DEFAULT_LAYER_STATE.radarSourceCode,
    radarProductCode: radarProductCode || DEFAULT_LAYER_STATE.radarProductCode,
    weatherRadarLegendCollapsed: Boolean(weatherRadarLegendCollapsed),
    weatherRadarContoursEnabled: Boolean(weatherRadarContoursEnabled),
    weatherRadarCellsEnabled: Boolean(weatherRadarCellsEnabled),
    weatherRadarCellTracksEnabled: Boolean(weatherRadarCellTracksEnabled),
    weatherRadarNowcastEnabled: Boolean(weatherRadarNowcastEnabled),
    colombiaRadarNowcastEnabled: Boolean(
      appState.colombiaRadarNowcastEnabled ?? DEFAULT_LAYER_STATE.colombiaRadarNowcastEnabled
    ),
    weatherRadarSiteRangeRingsEnabled: Boolean(weatherRadarSiteRangeRingsEnabled),
    weatherShowPlannedRadarSites: Boolean(
      appState.weatherShowPlannedRadarSites ?? DEFAULT_LAYER_STATE.weatherShowPlannedRadarSites
    ),
    weatherValleyFocusModeEnabled: valleyFocusModeEnabled,
    weatherFocusAreaCode:
      appState.weatherFocusAreaCode || DEFAULT_LAYER_STATE.weatherFocusAreaCode,
    weatherFocusBufferKm:
      appState.weatherFocusBufferKm != null
        ? Number(appState.weatherFocusBufferKm)
        : DEFAULT_LAYER_STATE.weatherFocusBufferKm,
    weatherValleyFocusLimitOverlays: Boolean(
      appState.weatherValleyFocusLimitOverlays ?? DEFAULT_LAYER_STATE.weatherValleyFocusLimitOverlays
    ),
    weatherValleyFocusShowBoundary: Boolean(
      appState.weatherValleyFocusShowBoundary ?? DEFAULT_LAYER_STATE.weatherValleyFocusShowBoundary
    ),
    weatherValleyFocusShowBuffer: Boolean(
      appState.weatherValleyFocusShowBuffer ?? DEFAULT_LAYER_STATE.weatherValleyFocusShowBuffer
    ),
    weatherValleyFocusDimOutside: Boolean(
      appState.weatherValleyFocusDimOutside ?? DEFAULT_LAYER_STATE.weatherValleyFocusDimOutside
    ),
    weatherRainGaugesEnabled: rainGaugesOverlayEnabled,
    rainfallIntensity: Boolean(rainfallIntensityEnabled),
    rainfallAccumulation: Boolean(rainfallAccumulationEnabled),
    floodZones: Boolean(floodZonesEnabled),
    hydro: Boolean(hydroEnabled),
    stormCells: Boolean(stormCellsEnabled),
    stormTracks: Boolean(stormTracksEnabled),
    stormProjections: Boolean(stormProjectionsEnabled),
    rainGauges: Boolean(rainGaugesEnabled),
    weatherStations: Boolean(weatherStationsEnabled),
    streamSensors: Boolean(streamSensorsEnabled),
    officialAlerts: Boolean(officialAlertsEnabled),
    midpointAlerts: Boolean(midpointAlertsEnabled),
    communityReports: Boolean(communityReportsEnabled),
    precipitationStations: Boolean(precipitationStationsEnabled),
    riverLevels: Boolean(riverLevelsEnabled),
    environmentalAlerts: Boolean(environmentalAlertsEnabled),
    siataAirQuality: Boolean(siataAirQualityEnabled),
    radarLayer: Boolean(radarLayerEnabled),
    ideamRadarLayer: Boolean(appState.ideamRadarLayerEnabled),
    ideamBartOverlay: Boolean(appState.ideamBartOverlayEnabled),
    siataSantaElenaOverlay: Boolean(appState.siataSantaElenaOverlayEnabled),
    aerocivilAviationOverlay: Boolean(appState.aerocivilAviationOverlayEnabled),
    rainviewerBackupRadar: Boolean(appState.rainviewerBackupLayerEnabled),
    satellitePrecipLayer: Boolean(appState.satellitePrecipLayerEnabled),
    lightningStrikes: Boolean(lightningStrikesEnabled),
    forecastCards: Boolean(forecastCardsEnabled),
    neighborhoodRiskBadges: Boolean(neighborhoodRiskBadgesEnabled),
    floodRiskSurface: Boolean(floodRiskSurfaceEnabled),
    communitySignals: Boolean(communitySignalsEnabled),
    potentialEvents: Boolean(potentialEventsEnabled),
    predictiveSignals: Boolean(predictiveSignalsEnabled),
    weatherAutoRefresh: Boolean(weatherAutoRefresh),
    weatherAlertsActiveOnly: Boolean(weatherAlertsActiveOnly),
    infrastructureTypes: [...(selectedInfrastructureTypes || [])],
    boundaryBarrios: Boolean(boundaryBarriosEnabled),
    boundaryComunas: Boolean(boundaryComunasEnabled),
    boundaryMarketZones: Boolean(boundaryMarketZonesEnabled),
    foodSupermarkets: Boolean(foodSupermarketsEnabled),
    foodSupermarketBuffers: Boolean(foodSupermarketBuffersEnabled),
    foodPriceHeatmap: Boolean(foodPriceHeatmapEnabled),
    foodBasicBasketZones: Boolean(foodBasicBasketZonesEnabled),
    foodAccessGaps: Boolean(foodAccessGapsEnabled),
    transportMetroStations: Boolean(transportMetroStationsEnabled),
    transportMetroLines: Boolean(transportMetroLinesEnabled),
    transportBusStops: Boolean(transportBusStopsEnabled),
    transportBusRoutes: Boolean(transportBusRoutesEnabled),
    transportOsmCommunity: Boolean(transportOsmCommunityEnabled),
    transportDiagnostics: Boolean(transportDiagnosticsEnabled),
    transportMetroRidershipPressure: Boolean(transportMetroRidershipPressureEnabled),
    transportDisruptionSignals: Boolean(transportDisruptionSignalsEnabled),
    transportDisruptionWeatherAlerts: Boolean(transportDisruptionWeatherAlertsEnabled),
    transportDisruptionFloodContext: Boolean(transportDisruptionFloodContextEnabled),
    transportDisruptionRainRisk: Boolean(transportDisruptionRainRiskEnabled),
    transportCommunityMobilityReports: Boolean(transportCommunityMobilityReportsEnabled),
    transportCommunityVerifiedReports: Boolean(transportCommunityVerifiedReportsEnabled),
    transportCommunityUnverifiedReports: Boolean(transportCommunityUnverifiedReportsEnabled),
    radarOpacity:
      radarOpacity != null && Number.isFinite(Number(radarOpacity))
        ? Number(radarOpacity)
        : DEFAULT_LAYER_STATE.radarOpacity,
    satellitePrecipOpacity:
      satellitePrecipOpacity != null && Number.isFinite(Number(satellitePrecipOpacity))
        ? Number(satellitePrecipOpacity)
        : DEFAULT_LAYER_STATE.satellitePrecipOpacity,
    colombiaRadarSourceMode: COLOMBIA_RADAR_SOURCE_MODES.has(colombiaRadarSourceMode)
      ? colombiaRadarSourceMode
      : DEFAULT_LAYER_STATE.colombiaRadarSourceMode,
    colombiaRadarSourceModeOrigin: COLOMBIA_RADAR_SOURCE_MODE_ORIGINS.has(
      colombiaRadarSourceModeOrigin
    )
      ? colombiaRadarSourceModeOrigin
      : DEFAULT_LAYER_STATE.colombiaRadarSourceModeOrigin,
    weatherActiveNavId: weatherActiveNavId || "",
    weatherTimeMode: weatherTimeMode === "replay" ? "replay" : "live",
    weatherRadarMode: weatherRadarMode || DEFAULT_LAYER_STATE.weatherRadarMode,
    weatherRadarPlaybackRangePreset:
      weatherRadarPlaybackRangePreset || DEFAULT_LAYER_STATE.weatherRadarPlaybackRangePreset,
    rainfallAccumulationWindow:
      rainfallAccumulationWindow || DEFAULT_LAYER_STATE.rainfallAccumulationWindow,
    rainfallOpacity:
      rainfallOpacity != null && Number.isFinite(Number(rainfallOpacity))
        ? Number(rainfallOpacity)
        : DEFAULT_LAYER_STATE.rainfallOpacity,
    lightningSinceMinutes:
      lightningSinceMinutes != null && Number.isFinite(Number(lightningSinceMinutes))
        ? Number(lightningSinceMinutes)
        : DEFAULT_LAYER_STATE.lightningSinceMinutes,
    localSensorsPanelEnabled: Boolean(localSensorsPanelEnabled),
    stormReplayModeEnabled: Boolean(stormReplayModeEnabled),
    stormReplaySourceCode: stormReplaySourceCode || "",
    stormReplayProductCode: stormReplayProductCode || "",
    stormReplayTimeBucketMinutes:
      stormReplayTimeBucketMinutes != null
        ? Number(stormReplayTimeBucketMinutes)
        : DEFAULT_LAYER_STATE.stormReplayTimeBucketMinutes,
    stormReplayIncludeLayers:
      stormReplayIncludeLayers && typeof stormReplayIncludeLayers === "object"
        ? stormReplayIncludeLayers
        : DEFAULT_LAYER_STATE.stormReplayIncludeLayers,
    forecastChartRangeHours: forecastChartRangeHours === 48 ? 48 : 24,
    historicalChartRangeDays: historicalChartRangeDays === 30 ? 30 : 7,
    weatherMapLegendVisibility:
      weatherMapLegendVisibility && typeof weatherMapLegendVisibility === "object"
        ? weatherMapLegendVisibility
        : DEFAULT_LAYER_STATE.weatherMapLegendVisibility,
  };
}
